import { useMemo } from "react";
import toast from "react-hot-toast";
import PostTrialSurveyModal from "./PostTrialSurveyModal";
import { SurveyGateContext } from "./SurveyGateContext";
import { usePostTrialSurvey } from "./usePostTrialSurvey";

// Owns the survey state and renders the modal. Mounted once in Dashboard
// (final-week prompt) and once on the paywall page (post-expiry prompt);
// eligibility is fetched once per page load regardless.
export const PostTrialSurveyProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const survey = usePostTrialSurvey();
  const gate = useMemo(
    () => ({ surveyOpen: survey.open, surveyResolved: survey.resolved }),
    [survey.open, survey.resolved]
  );

  const wrapped = useMemo(
    () => ({
      ...survey,
      complete: async () => {
        try {
          await survey.complete();
          toast.success("Thanks — your feedback is in.");
        } catch (err: any) {
          toast.error(err?.response?.data?.error || "Couldn't submit the survey. Please try again.");
          throw err;
        }
      },
      postpone: async () => {
        try {
          const until = await survey.postpone();
          toast.success(
            `No problem. We'll ask again on ${until.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}.`
          );
          return until;
        } catch (err: any) {
          toast.error(err?.response?.data?.error === "SURVEY_CANNOT_POSTPONE"
            ? "This survey can't be postponed any longer."
            : "Couldn't postpone the survey. Please try again.");
          throw err;
        }
      },
    }),
    [survey]
  );

  return (
    <SurveyGateContext.Provider value={gate}>
      {children}
      <PostTrialSurveyModal survey={wrapped} />
    </SurveyGateContext.Provider>
  );
};

export default PostTrialSurveyProvider;
